import {PageAnimation} from '@PosterWhiteboard/page/animation/page-animation.class';
import {AnimateItemType, type AnimationObject, AnimationType, getAnimationTime} from '@PosterWhiteboard/classes/item-animation.class';
import {isAnimationApplicableOnItem} from '@PosterWhiteboard/animation/animation.class';
import {showMessageGrowl} from '@Components/message-growl';
import type {ItemType} from '@PosterWhiteboard/items/item/item.types';

const START_TIME_LEEWAY = 3;
export class PageIntroAnimation extends PageAnimation {
  public replayPoster(): void {
    void this.page.poster.replayPoster();
  }

  private showAnimationReplacedMessage(oldAnimation: AnimationObject): void {
    let showAnimationsReplacedMessage = false;
    Object.keys(this.page.items.itemsHashMap).forEach((key) => {
      const item = this.page.items.itemsHashMap[key];
      if (
        item.hasIntroAnimation() &&
        (item.introAnimation.type !== oldAnimation.type || item.introAnimation.speed !== oldAnimation.speed || item.introAnimation.direction !== oldAnimation.direction)
      ) {
        showAnimationsReplacedMessage = true;
        return;
      }
    });
    if (showAnimationsReplacedMessage) {
      showMessageGrowl({
        text: window.i18next.t('pmwjs_animations_replaced'),
        interval: 2000,
      });
    }
  }

  public updatePageItemsAnimation(oldAnimation: AnimationObject | null): void {
    if (oldAnimation) {
      this.showAnimationReplacedMessage(oldAnimation);
    }
    Object.keys(this.page.items.itemsHashMap).forEach((key) => {
      const item = this.page.items.itemsHashMap[key];
      if (isAnimationApplicableOnItem(item, this.type)) {
        void item.updateFromObject({introAnimation: this.toObject()}, {undoable: false, updateRedux: false, doInvalidate: false});
      } else if (item.hasIntroAnimation()) {
        void item.updateFromObject({introAnimation: {type: AnimationType.NONE}}, {undoable: false, updateRedux: false, doInvalidate: false});
      }
    });
    this.updateItemDuration();
  }

  public getStartTimes(): Record<string, number> {
    const startTimeHashMap: Record<string, number> = {};
    Object.keys(this.page.items.itemsHashMap).forEach((key) => {
      const currentItem = this.page.items.itemsHashMap[key];
      if (this.itemHasDifferentStartTime(currentItem)) {
        startTimeHashMap[currentItem.uid] = currentItem.onPageStartTime;
        return;
      }
      switch (currentItem.introAnimation.type) {
        case AnimationType.SLIDE:
          startTimeHashMap[currentItem.uid] = this.page.items.isVideoItemUsedAsBackground()
            ? this.getDelayForModelType(currentItem.gitype, getAnimationTime(currentItem.introAnimation.type, currentItem.introAnimation.speed))
            : 0;
          break;
        case AnimationType.RISE: {
          const items = this.getSortedGraphicItemsAccordingToPosition(AnimateItemType.INTRO_ANIMATION);
          startTimeHashMap[currentItem.uid] =
            this.getDelayFactorByTotalObjects(getAnimationTime(currentItem.introAnimation.type, currentItem.introAnimation.speed)) *
            items.findIndex((item) => currentItem.uid === item.uid);
          break;
        }
        case AnimationType.BLOCK: {
          const items = this.getSortedGraphicItemsAccordingToPosition(AnimateItemType.INTRO_ANIMATION).filter(
            (item) => item.isText() || item.isSlideshow()
          );
          if (items.length) {
            startTimeHashMap[currentItem.uid] =
              this.getAlternativeDelayFactor(items.length, getAnimationTime(currentItem.introAnimation.type, currentItem.introAnimation.speed)) *
              items.findIndex((item) => currentItem.uid === item.uid);
          }
          break;
        }
        case AnimationType.TUMBLE: {
          const items = this.getSortedGraphicItemsAccordingToPosition(AnimateItemType.INTRO_ANIMATION);
          startTimeHashMap[currentItem.uid] =
            this.getAlternativeDelayFactor(items.length, getAnimationTime(currentItem.introAnimation.type, currentItem.introAnimation.speed)) *
            items.findIndex((item) => currentItem.uid === item.uid);
          void currentItem.updateFromObject(
            {introAnimation: {direction: this.getTumbleAnimationDirection(currentItem)}},
            {doInvalidate: false, undoable: false, updateRedux: false}
          );
          break;
        }
        case AnimationType.PEEK:
        case AnimationType.WIPE:
        case AnimationType.PAN: {
          const items = this.getSortedGraphicItemsAccordingToPosition(AnimateItemType.INTRO_ANIMATION);
          startTimeHashMap[currentItem.uid] =
            this.getAlternativeDelayFactor(items.length, getAnimationTime(currentItem.introAnimation.type, currentItem.introAnimation.speed)) *
            items.findIndex((item) => currentItem.uid === item.uid);
          break;
        }
        case AnimationType.POP:
        case AnimationType.JELLO: {
          const items = this.getSortedGraphicItemsAccordingToZIndex(AnimateItemType.INTRO_ANIMATION);
          startTimeHashMap[currentItem.uid] =
            this.getAlternativeDelayFactor(items.length, getAnimationTime(currentItem.introAnimation.type, currentItem.introAnimation.speed)) *
            items.findIndex((item) => currentItem.uid === item.uid);
          break;
        }
        case AnimationType.ROTATE: {
          const items = this.getSortedGraphicItemsAccordingToZIndex(AnimateItemType.INTRO_ANIMATION);
          startTimeHashMap[currentItem.uid] =
            this.getAlternativeDelayFactor(items.length, getAnimationTime(currentItem.introAnimation.type, currentItem.introAnimation.speed)) *
            items.findIndex((item) => currentItem.uid === item.uid);
          break;
        }
        case AnimationType.BOUNCE:
        case AnimationType.BOUNCE_IN_DOWN:
        case AnimationType.FADE:
          startTimeHashMap[currentItem.uid] = this.getDelayForModelType(currentItem.gitype, getAnimationTime(currentItem.introAnimation.type, currentItem.introAnimation.speed));
          break;
        case AnimationType.NONE:
        case AnimationType.SCALE:
        case AnimationType.ZOOM:
        default:
          startTimeHashMap[currentItem.uid] = 0;
          break;
      }
    });
    this.positionSortedItems = [];
    this.zIndexSortedItems = [];
    return startTimeHashMap;
  }

  private itemHasDifferentStartTime(item: ItemType): boolean {
    return item.isTranscript() || (item.isSlideshow() && item.hasIntroAnimation() && item.onPageStartTime > START_TIME_LEEWAY);
  }

  public updateItemDuration(): void {
    const startTimes = this.getStartTimes();
    Object.keys(this.page.items.itemsHashMap).forEach((key) => {
      const item = this.page.items.itemsHashMap[key];
      if (this.isAnimationDurationApplicableOnItem(item)) {
        void item.updateFromObject({onPageStartTime: startTimes[key]}, {undoable: false, doInvalidate: false, updateRedux: false});
      }
    });
  }
}
